<template>
	<div
		class="bg-fill-tertiary relative mt-3 scroll-m-15 rounded-2xl p-1"
		ref="rouletteBlock"
	>
		<slot name="header" />
		<div class="relative overflow-hidden rounded-xl bg-black">
			<ItemRoulette
				horizontal
				:casePrice="props.price"
				:items="items"
				:noDrift="noDrift"
				:seed="seed"
				ref="itemRoulette1"
				v-if="count === 1"
			/>

			<div class="flex items-stretch" v-if="count === 2">
				<slot name="player1">
					<ItemRoulette
						:casePrice="props.price"
						:items="items"
						:noDrift="noDrift"
						:seed="seed"
						ref="itemRoulette1"
					/>
				</slot>
				<div
					class="via-fill-tertiary relative w-px shrink-0 bg-gradient-to-b from-black to-black *:z-2"
				>
					<slot name="vs" />
				</div>
				<slot name="player2">
					<ItemRoulette
						:casePrice="props.price"
						:items="items"
						:noDrift="noDrift"
						:seed="seed"
						ref="itemRoulette2"
						:startOffset="1"
					/>
				</slot>
			</div>

			<div class="flex items-stretch" v-if="count === 3">
				<slot name="player1">
					<ItemRoulette
						:casePrice="props.price"
						:items="items"
						:noDrift="noDrift"
						:seed="seed"
						ref="itemRoulette1"
					/>
				</slot>
				<div
					class="via-fill-tertiary relative w-px shrink-0 bg-gradient-to-b from-black to-black *:z-2"
				>
					<slot name="vs" />
				</div>
				<slot name="player2">
					<ItemRoulette
						:casePrice="props.price"
						:items="items"
						:noDrift="noDrift"
						:seed="seed"
						ref="itemRoulette2"
						:startOffset="1"
					/>
				</slot>
				<div
					class="via-fill-tertiary relative w-px shrink-0 bg-gradient-to-b from-black to-black *:z-2"
				>
					<slot name="vs" />
				</div>
				<slot name="player3">
					<ItemRoulette
						:casePrice="props.price"
						:items="items"
						:noDrift="noDrift"
						:seed="seed"
						ref="itemRoulette3"
						:startOffset="2"
					/>
				</slot>
			</div>
			<template v-if="count === 4">
				<div class="flex items-stretch">
					<slot name="player1">
						<ItemRoulette
							:casePrice="props.price"
							:items="items"
							:noDrift="noDrift"
							:seed="seed"
							ref="itemRoulette1"
						/>
					</slot>
					<div
						class="via-fill-tertiary w-px shrink-0 bg-gradient-to-b from-black to-black"
					/>
					<slot name="player2">
						<ItemRoulette
							:casePrice="props.price"
							:items="items"
							:noDrift="noDrift"
							:seed="seed"
							ref="itemRoulette2"
							:startOffset="1"
						/>
					</slot>
				</div>
				<div class="bg-fill-tertiary relative h-px w-full shrink-0 *:z-2">
					<slot name="vs" />
				</div>
				<div class="flex items-stretch">
					<slot name="player3">
						<ItemRoulette
							:casePrice="props.price"
							:items="items"
							:noDrift="noDrift"
							:seed="seed"
							ref="itemRoulette3"
							:startOffset="2"
						/>
					</slot>
					<div
						class="via-fill-tertiary w-px shrink-0 bg-gradient-to-b from-black to-black"
					/>
					<slot name="player4">
						<ItemRoulette
							:casePrice="props.price"
							:items="items"
							:noDrift="noDrift"
							:seed="seed"
							ref="itemRoulette4"
							:startOffset="3"
						/>
					</slot>
				</div>
			</template>
			<slot name="footer" />
			<Transition
				enterActiveClass="transition-all"
				leaveActiveClass="transition-all"
				enterFromClass="-translate-x-full -translate-y-full opacity-0"
				leaveToClass="-translate-x-full -translate-y-full opacity-0"
			>
				<CornerIcon
					class="text-fill-tertiary absolute top-3 left-3 z-9 h-5 w-5 rotate-90"
					v-if="corners"
				/>
			</Transition>
			<Transition
				enterActiveClass="transition-all"
				leaveActiveClass="transition-all"
				enterFromClass="-translate-x-full translate-y-full opacity-0"
				leaveToClass="-translate-x-full translate-y-full opacity-0"
			>
				<CornerIcon
					class="text-fill-tertiary absolute bottom-3 left-3 z-9 h-5 w-5"
					v-if="corners"
				/>
			</Transition>
			<Transition
				enterActiveClass="transition-all"
				leaveActiveClass="transition-all"
				enterFromClass="translate-x-full -translate-y-full opacity-0"
				leaveToClass="translate-x-full -translate-y-full opacity-0"
			>
				<CornerIcon
					class="text-fill-tertiary absolute top-3 right-3 z-9 h-5 w-5 rotate-180"
					v-if="corners"
				/>
			</Transition>
			<Transition
				enterActiveClass="transition-all"
				leaveActiveClass="transition-all"
				enterFromClass="translate-x-full translate-y-full opacity-0"
				leaveToClass="translate-x-full translate-y-full opacity-0"
			>
				<CornerIcon
					class="text-fill-tertiary absolute right-3 bottom-3 z-9 h-5 w-5 -rotate-90"
					v-if="corners"
				/>
			</Transition>
		</div>
	</div>
</template>
<script setup lang="ts">
import { ref } from 'vue'
import ItemRoulette from './ItemRoulette.vue'
import CornerIcon from '@/assets/icons/corner.svg'

const props = defineProps<{
	count: number
	price: number
	fast?: boolean
	scrollIntoView?: boolean
	items: [string, string | null | undefined, number][]
	corners?: boolean
	noDrift?: boolean
	seed?: string
}>()

const rouletteBlock = ref()
const itemRoulette1 = ref<InstanceType<typeof ItemRoulette>>()
const itemRoulette2 = ref<InstanceType<typeof ItemRoulette>>()
const itemRoulette3 = ref<InstanceType<typeof ItemRoulette>>()
const itemRoulette4 = ref<InstanceType<typeof ItemRoulette>>()

const spin = async (
	items: {
		src: string
		imgSrc: string
		preloader?: string | null
		price: number
		chance: number
	}[],
	pullbackCallback?: (index: number) => void
) => {
	if (!items.length) {
		return
	}

	if (rouletteBlock.value && props.scrollIntoView) {
		rouletteBlock.value.scrollIntoView({
			behavior: 'smooth'
		})
	}

	const promises = []

	if (items[0] && itemRoulette1.value) {
		promises.push(
			itemRoulette1.value.roll(
				items[0],
				props.fast,
				pullbackCallback ? () => pullbackCallback(0) : undefined
			)
		)
	}

	if (items[1] && itemRoulette2.value) {
		promises.push(
			itemRoulette2.value.roll(
				items[1],
				props.fast,
				pullbackCallback ? () => pullbackCallback(1) : undefined
			)
		)
	}

	if (items[2] && itemRoulette3.value) {
		promises.push(
			itemRoulette3.value.roll(
				items[2],
				props.fast,
				pullbackCallback ? () => pullbackCallback(2) : undefined
			)
		)
	}

	if (items[3] && itemRoulette4.value) {
		promises.push(
			itemRoulette4.value.roll(
				items[3],
				props.fast,
				pullbackCallback ? () => pullbackCallback(3) : undefined
			)
		)
	}

	await Promise.all(promises)
}

defineExpose({
	spin
})
</script>
